import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DTRRule, DTRRuleConverted } from '../types';

import { convertValueForSave } from './utils/convertValueForSave';
import { getSchema } from './utils/schemas';

import { LoadingButton } from '@mui/lab';
import { Box, Skeleton, TextField } from '@mui/material';

import {
  ControlledCheckbox,
  ControlledCheckboxAutoComplete
} from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { useSaveDTRRule } from '../hooks/useSaveDTRRule';
import { RulesRenderer } from './RulesRenderer';
import { useGetDccOptions } from './hooks/useGetDccOptions';

interface Props {
  handleClose: () => void;
  rowData: DTRRuleConverted;
}

const DCC_IDS = 'dccIds';

export const EditDTRRulesForm: React.FC<Props> = ({ rowData, handleClose }) => {
  const { t } = useTranslation();
  const { isFetching, data: options } = useGetDccOptions();
  const methods = useForm<DTRRule>({
    resolver: yupResolver(getSchema(rowData)),
    defaultValues: rowData,
    mode: 'onChange'
  });
  const onRemoveElement = (id: number | string) => {
    methods.setValue(
      DCC_IDS,
      methods.watch(DCC_IDS).filter((sid: number) => sid !== id),
      { shouldDirty: true }
    );
  };
  const { mutate, isLoading } = useSaveDTRRule(handleClose);

  const onSubmit = async (data) => {
    await mutate(convertValueForSave(data));
  };

  return (
    <MultiStepForm onSubmit={methods.handleSubmit(onSubmit)} methods={methods}>
      <div style={{ marginTop: 10 }}>
        <TextField
          name="name"
          value={t(`dtrRules.rule.${rowData.name}`)}
          label={t(`name`)}
          fullWidth
          disabled
        />
        <ControlledCheckbox
          control={methods.control}
          name={'enabled'}
          label={t('dtrRules.editRule.enabled')}
        />
        <RulesRenderer />
        {isFetching ? (
          <Skeleton />
        ) : (
          <ControlledCheckboxAutoComplete
            fullWidth
            name={DCC_IDS}
            label={t('dtrRules.dutyType')}
            options={options ?? []}
            control={methods.control}
            idProperty="value"
            labelProperty="label"
            allowItemRemoval
            onRemoveElement={onRemoveElement}
          />
        )}
      </div>
      <MultiStepForm.Actions>
        <Box display="flex" ml="auto">
          <LoadingButton
            loading={isLoading}
            type="submit"
            variant="contained"
            autoFocus
            color="dark"
          >
            {t('save')}
          </LoadingButton>
        </Box>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};
